import { useEffect, useState } from 'react';
import api from '../services/api';

interface EmailAccount {
  id: string;
  client_label: string;
  email_address: string;
  is_active: boolean;
  last_scanned_at: string | null;
  created_at: string;
  gmail_connected?: boolean;
}

interface DigestData {
  generated_at: string;
  user_name: string;
  total_clients: number;
  total_tasks: number;
  total_estimated_minutes: number;
  by_client: Record<string, Array<{ title: string; priority: string; due_date: string | null; suggested_time_minutes: number | null }>>;
  client_summaries: Record<string, string>;
}

interface ExtractedTask {
  id: string;
  client_label: string | null;
  source_email_subject: string | null;
  source_email_from: string | null;
  task_title: string;
  task_description: string | null;
  priority: string;
  suggested_time_minutes: number | null;
  due_date: string | null;
  status: string;
  created_at: string;
  google_calendar_event_id?: string | null;
  google_tasks_task_id?: string | null;
}

interface GoogleStatus {
  connected: boolean;
  scopes: string[];
  has_gmail: boolean;
  has_calendar: boolean;
  has_tasks: boolean;
  has_drive: boolean;
}

interface SampleEmailInput {
  from: string;
  subject: string;
  body: string;
  date: string;
  client_label: string;
}

const DEFAULT_SAMPLE: SampleEmailInput = {
  from: '',
  subject: '',
  body: '',
  date: '',
  client_label: '',
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  medium: 'bg-blue-100 text-blue-800 border-blue-300',
  low: 'bg-gray-100 text-gray-700 border-gray-300',
};

export default function FreelancerDashboard() {
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [tasks, setTasks] = useState<ExtractedTask[]>([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [newAccount, setNewAccount] = useState({ client_label: '', email_address: '' });
  const [sampleEmails, setSampleEmails] = useState<SampleEmailInput[]>([{ ...DEFAULT_SAMPLE }]);
  const [scanError, setScanError] = useState<string | null>(null);
  const [scanResult, setScanResult] = useState<{ extracted: number } | null>(null);
  const [digest, setDigest] = useState<DigestData | null>(null);
  const [digestOpen, setDigestOpen] = useState(false);
  const [gmailScanning, setGmailScanning] = useState(false);
  const [gmailStatus, setGmailStatus] = useState<string | null>(null);
  // Last scan audit: how many emails were fetched, how many were classified as
  // TASK/FYI/NOISE. Rendered under the Morning Brief header as the credibility
  // signal that Charlotte is filtering — not hallucinating.
  const [lastScan, setLastScan] = useState<{
    emails: number;
    tasks: number;
    fyi: number;
    noise: number;
  } | null>(null);
  const [googleStatus, setGoogleStatus] = useState<GoogleStatus | null>(null);
  const [taskActionPending, setTaskActionPending] = useState<Record<string, string>>({});
  // Local optimistic markers so demo shows ✓ even if task list reloads slowly
  const [scheduledLocal, setScheduledLocal] = useState<Record<string, boolean>>({});
  const [taskedLocal, setTaskedLocal] = useState<Record<string, boolean>>({});

  const loadAccounts = async () => {
    try {
      const res = await api.get('/freelancer/accounts');
      setAccounts(res.data || []);
    } catch (e) {
      console.error('Failed to load accounts', e);
    }
  };

  const loadTasks = async () => {
    try {
      const res = await api.get('/freelancer/tasks?status_filter=pending');
      setTasks(res.data || []);
    } catch (e) {
      console.error('Failed to load tasks', e);
    }
  };

  const loadDigest = async () => {
    try {
      const res = await api.get('/freelancer/digest');
      setDigest(res.data);
    } catch (e) {
      console.error('Failed to load digest', e);
    }
  };

  const loadGoogleStatus = async () => {
    try {
      const res = await api.get('/freelancer/google-status');
      setGoogleStatus(res.data);
    } catch (e) {
      console.error('Failed to load google status', e);
    }
  };

  const handleScheduleTask = async (taskId: string) => {
    setTaskActionPending((p) => ({ ...p, [taskId]: 'schedule' }));
    try {
      const res = await api.post(`/freelancer/tasks/${taskId}/schedule`);
      setScheduledLocal((s) => ({ ...s, [taskId]: true }));
      setGmailStatus(
        `Scheduled on Calendar: ${new Date(res.data.start).toLocaleString()}`
      );
    } catch (e: any) {
      setGmailStatus(`Schedule failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTaskActionPending((p) => {
        const n = { ...p };
        delete n[taskId];
        return n;
      });
    }
  };

  const handleAddToTasklist = async (taskId: string) => {
    setTaskActionPending((p) => ({ ...p, [taskId]: 'tasks' }));
    try {
      await api.post(`/freelancer/tasks/${taskId}/add-to-tasklist`);
      setTaskedLocal((s) => ({ ...s, [taskId]: true }));
      setGmailStatus('Added to Google Tasks.');
    } catch (e: any) {
      setGmailStatus(`Add to Tasks failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setTaskActionPending((p) => {
        const n = { ...p };
        delete n[taskId];
        return n;
      });
    }
  };

  const handleDismissTask = async (taskId: string) => {
    try {
      await api.patch(`/freelancer/tasks/${taskId}`, { status: 'dismissed' });
      await loadTasks();
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([loadAccounts(), loadTasks(), loadDigest(), loadGoogleStatus()]);
      setLoading(false);
    })();

    // Check for ?gmail_connected=1 or ?error= after OAuth redirect
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail_connected')) {
      setGmailStatus('Gmail connected successfully.');
      window.history.replaceState({}, '', '/freelancer');
    } else if (params.get('error')) {
      setGmailStatus(`Gmail connect failed: ${params.get('error')}`);
      window.history.replaceState({}, '', '/freelancer');
    }
  }, []);

  const handleConnectGmail = async (accountId: string) => {
    try {
      const res = await api.get(`/freelancer/accounts/${accountId}/oauth/start`);
      if (res.data?.authorize_url) {
        window.location.href = res.data.authorize_url;
      } else {
        setGmailStatus('Failed to get Gmail authorize URL');
      }
    } catch (e: any) {
      setGmailStatus(`Connect failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // One-click flow: create a placeholder account row, then immediately kick
  // OAuth. The callback updates the row with the real email from Google.
  const handleAddMailboxOAuth = async () => {
    try {
      const placeholder = {
        client_label: 'New mailbox (connecting...)',
        email_address: `pending-${Date.now()}@pending.local`,
      };
      const created = await api.post('/freelancer/accounts', placeholder);
      const newId = created.data?.id;
      if (!newId) {
        setGmailStatus('Failed to create account row');
        return;
      }
      // Kick OAuth for this new row
      const res = await api.get(`/freelancer/accounts/${newId}/oauth/start`);
      if (res.data?.authorize_url) {
        window.location.href = res.data.authorize_url;
      } else {
        setGmailStatus('Failed to get Gmail authorize URL');
      }
    } catch (e: any) {
      setGmailStatus(`Add mailbox failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  // Connect Daniel's primary Google account — redirects to KOG Google SSO
  // login flow which already requests full Gmail/Calendar/Drive/Tasks scopes.
  const handleConnectPrimaryGoogle = () => {
    window.location.href = '/api/v1/auth/google?next=/freelancer';
  };

  const handleGmailScan = async () => {
    setGmailStatus(null);
    setGmailScanning(true);
    try {
      const res = await api.post('/freelancer/scan/gmail');
      setLastScan({
        emails: res.data.emails_fetched ?? res.data.emails_scanned ?? 0,
        tasks: res.data.tasks ?? 0,
        fyi: res.data.fyi ?? 0,
        noise: res.data.noise ?? 0,
      });
      await Promise.all([loadTasks(), loadDigest(), loadAccounts()]);
    } catch (e: any) {
      setGmailStatus(`Scan failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setGmailScanning(false);
    }
  };

  const handleAddAccount = async () => {
    if (!newAccount.client_label || !newAccount.email_address) return;
    try {
      await api.post('/freelancer/accounts', newAccount);
      setNewAccount({ client_label: '', email_address: '' });
      setShowAddAccount(false);
      await loadAccounts();
    } catch (e: any) {
      alert(`Failed to add account: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (!confirm('Remove this client email account?')) return;
    try {
      await api.delete(`/freelancer/accounts/${id}`);
      await loadAccounts();
    } catch (e) {
      console.error(e);
    }
  };

  const handleRunScan = async () => {
    setScanError(null);
    setScanResult(null);
    setScanning(true);
    try {
      const payload = {
        sample_emails: sampleEmails
          .filter((e) => e.subject || e.body)
          .map((e) => ({
            from: e.from || null,
            subject: e.subject || null,
            body: e.body || null,
            date: e.date || null,
            client_label: e.client_label || null,
          })),
      };
      if (payload.sample_emails.length === 0) {
        setScanError('Add at least one sample email (subject or body required)');
        setScanning(false);
        return;
      }
      const res = await api.post('/freelancer/scan', payload);
      setScanResult({ extracted: res.data.extracted_task_count });
      setLastScan({
        emails: res.data.emails_scanned ?? 0,
        tasks: res.data.tasks ?? 0,
        fyi: res.data.fyi ?? 0,
        noise: res.data.noise ?? 0,
      });
      await loadTasks();
    } catch (e: any) {
      setScanError(e?.response?.data?.detail || e.message);
    } finally {
      setScanning(false);
    }
  };

  const handleTaskDone = async (id: string) => {
    try {
      await api.patch(`/freelancer/tasks/${id}`, { status: 'done' });
      await loadTasks();
    } catch (e) {
      console.error(e);
    }
  };

  // Group tasks by client_label
  const tasksByClient: Record<string, ExtractedTask[]> = {};
  tasks.forEach((t) => {
    const key = t.client_label || '(unassigned)';
    if (!tasksByClient[key]) tasksByClient[key] = [];
    tasksByClient[key].push(t);
  });

  const totalMinutes = tasks.reduce((sum, t) => sum + (t.suggested_time_minutes || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Morning Brief</h1>
          <p className="text-gray-700 mt-1">
            One view across every client. Charlotte filters the noise so you only see work
            that needs you.
          </p>
        </div>

        {/* Morning Brief summary panel */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg p-5 mb-6 shadow">
          <div className="flex justify-between items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl font-semibold">
                {tasks.length === 0
                  ? "You're all clear."
                  : `You have ${tasks.length} thing${tasks.length !== 1 ? 's' : ''} that actually need${tasks.length === 1 ? 's' : ''} your attention.`}
              </h2>
              <p className="text-indigo-100 mt-1 text-sm">
                {tasks.length === 0
                  ? `No pending work across ${Object.keys(tasksByClient).length || accounts.length || 0} client${(Object.keys(tasksByClient).length || accounts.length || 0) !== 1 ? 's' : ''}.`
                  : `Across ${Object.keys(tasksByClient).length} client${Object.keys(tasksByClient).length !== 1 ? 's' : ''}${totalMinutes > 0 ? ` — roughly ${Math.round((totalMinutes / 60) * 10) / 10}h of focused work` : ''}.`}
              </p>
              {lastScan && (
                <div className="mt-3 text-xs text-indigo-100 bg-indigo-900/30 rounded px-3 py-1.5 inline-block border border-indigo-300/30">
                  Scanned {lastScan.emails} email{lastScan.emails !== 1 ? 's' : ''}
                  {' · '}
                  <span className="text-white font-semibold">{lastScan.tasks} real task{lastScan.tasks !== 1 ? 's' : ''}</span>
                  {' · '}
                  {lastScan.fyi} FYI
                  {' · '}
                  {lastScan.noise} noise
                </div>
              )}
              {digest && digest.total_tasks > 0 && (
                <button
                  onClick={() => setDigestOpen(!digestOpen)}
                  className="block mt-2 text-indigo-100 text-xs hover:underline"
                >
                  {digestOpen ? 'Hide per-client summary' : 'Show per-client summary'}
                </button>
              )}
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              {googleStatus && !googleStatus.has_gmail && (
                <button
                  onClick={handleConnectPrimaryGoogle}
                  className="bg-white text-indigo-700 px-4 py-2.5 rounded-md font-semibold hover:bg-indigo-50"
                  title="Sign in with Google to grant Gmail + Calendar + Drive + Tasks access"
                >
                  Connect Google
                </button>
              )}
              {googleStatus?.has_gmail && (
                <button
                  onClick={handleGmailScan}
                  disabled={gmailScanning}
                  className="bg-indigo-800 text-white border border-indigo-300 px-4 py-2.5 rounded-md font-medium hover:bg-indigo-900 disabled:opacity-50"
                  title="Pull recent unread messages from your connected Gmail accounts"
                >
                  {gmailScanning ? 'Scanning Gmail…' : 'Scan My Gmail'}
                </button>
              )}
              <button
                onClick={() => {
                  setShowScanModal(true);
                  setScanResult(null);
                  setScanError(null);
                }}
                className="bg-white text-indigo-700 px-5 py-2.5 rounded-md font-semibold hover:bg-indigo-50"
              >
                Paste Samples
              </button>
            </div>
          </div>

          {googleStatus && (
            <div className="mt-2 text-xs text-indigo-100 flex flex-wrap gap-3">
              <span>
                {googleStatus.has_gmail ? '✓' : '○'} Gmail
              </span>
              <span>
                {googleStatus.has_calendar ? '✓' : '○'} Calendar
              </span>
              <span>
                {googleStatus.has_tasks ? '✓' : '○'} Tasks
              </span>
              <span>
                {googleStatus.has_drive ? '✓' : '○'} Drive
              </span>
            </div>
          )}

          {gmailStatus && (
            <div className="mt-3 px-3 py-2 bg-indigo-900/40 rounded text-sm border border-indigo-300/40">
              {gmailStatus}
            </div>
          )}

          {digestOpen && digest && digest.total_tasks > 0 && (
            <div className="mt-4 pt-4 border-t border-indigo-400/40">
              <div className="grid grid-cols-3 gap-3 mb-3 text-sm">
                <div className="bg-indigo-800/40 rounded p-2">
                  <div className="text-indigo-200 text-xs uppercase tracking-wide">Clients</div>
                  <div className="text-2xl font-semibold">{digest.total_clients}</div>
                </div>
                <div className="bg-indigo-800/40 rounded p-2">
                  <div className="text-indigo-200 text-xs uppercase tracking-wide">Tasks</div>
                  <div className="text-2xl font-semibold">{digest.total_tasks}</div>
                </div>
                <div className="bg-indigo-800/40 rounded p-2">
                  <div className="text-indigo-200 text-xs uppercase tracking-wide">Est. time</div>
                  <div className="text-2xl font-semibold">
                    {Math.round((digest.total_estimated_minutes / 60) * 10) / 10}h
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {Object.entries(digest.client_summaries).map(([client, summary]) => (
                  <div
                    key={client}
                    className="flex items-start gap-2 bg-indigo-800/30 rounded p-2 text-sm"
                  >
                    <span className="font-semibold text-indigo-100 flex-shrink-0">
                      {client}:
                    </span>
                    <span className="text-indigo-50">{summary}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left: accounts */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-900">Client Inboxes</h3>
                <button
                  onClick={handleAddMailboxOAuth}
                  className="text-indigo-700 text-sm font-medium hover:underline"
                  title="Connect a Gmail account via Google OAuth"
                >
                  + Connect Mailbox
                </button>
              </div>

              {showAddAccount && (
                <div className="mb-3 p-3 bg-gray-50 rounded border border-gray-200">
                  <input
                    type="text"
                    placeholder="Client label (e.g., Acme Corp)"
                    value={newAccount.client_label}
                    onChange={(e) =>
                      setNewAccount({ ...newAccount, client_label: e.target.value })
                    }
                    className="w-full mb-2 px-2 py-1.5 text-sm border border-gray-300 rounded"
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={newAccount.email_address}
                    onChange={(e) =>
                      setNewAccount({ ...newAccount, email_address: e.target.value })
                    }
                    className="w-full mb-2 px-2 py-1.5 text-sm border border-gray-300 rounded"
                  />
                  <button
                    onClick={handleAddAccount}
                    className="w-full bg-indigo-600 text-white text-sm py-1.5 rounded hover:bg-indigo-700"
                  >
                    Save
                  </button>
                </div>
              )}

              {accounts.length === 0 && !loading && (
                <p className="text-sm text-gray-600">No client inboxes linked yet.</p>
              )}

              <ul className="space-y-2">
                {accounts.map((a) => (
                  <li
                    key={a.id}
                    className="p-2 border border-gray-200 rounded hover:bg-gray-50"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm truncate">
                          {a.client_label}
                        </div>
                        <div className="text-xs text-gray-600 truncate">{a.email_address}</div>
                      </div>
                      <button
                        onClick={() => handleDeleteAccount(a.id)}
                        className="text-gray-400 hover:text-red-600 text-xs ml-2"
                      >
                        ×
                      </button>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      {a.gmail_connected ? (
                        <span className="text-xs text-green-700 font-medium flex items-center gap-1">
                          <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                          Gmail connected
                        </span>
                      ) : (
                        <span className="text-xs text-gray-600">Not connected</span>
                      )}
                      <button
                        onClick={() => handleConnectGmail(a.id)}
                        className="text-xs text-indigo-700 hover:text-indigo-900 hover:underline font-medium"
                      >
                        {a.gmail_connected ? 'Reconnect' : 'Connect Gmail'}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Center: today's tasks grouped by client */}
          <div className="lg:col-span-3">
            {loading ? (
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-600">
                Loading…
              </div>
            ) : Object.keys(tasksByClient).length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                {lastScan ? (
                  <>
                    <p className="text-gray-900 text-lg font-semibold mb-2">
                      You're all clear.
                    </p>
                    <p className="text-gray-700 text-sm">
                      Charlotte scanned {lastScan.emails} email{lastScan.emails !== 1 ? 's' : ''} and
                      found nothing that needs your attention today.
                    </p>
                    <p className="text-gray-600 text-xs mt-2">
                      ({lastScan.fyi} FYI, {lastScan.noise} filtered as noise)
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-gray-700 font-medium mb-2">No pending tasks yet.</p>
                    <p className="text-gray-600 text-sm">
                      Click <strong>Scan My Gmail</strong> to pull recent mail, or paste samples.
                    </p>
                  </>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(tasksByClient).map(([client, clientTasks]) => (
                  <div key={client} className="bg-white rounded-lg shadow p-4">
                    <div className="flex justify-between items-center mb-3 border-b border-gray-200 pb-2">
                      <h3 className="font-semibold text-gray-900">{client}</h3>
                      <span className="text-xs text-gray-600">
                        {clientTasks.length} task{clientTasks.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <ul className="space-y-2">
                      {clientTasks.map((t) => (
                        <li
                          key={t.id}
                          className="flex items-start gap-3 p-2 rounded hover:bg-gray-50"
                        >
                          <button
                            onClick={() => handleTaskDone(t.id)}
                            className="mt-0.5 w-5 h-5 border-2 border-gray-400 rounded hover:border-green-600 hover:bg-green-50 flex-shrink-0"
                            title="Mark done"
                          />
                          <div className="flex-1 min-w-0">
                            {/* Task is a single line — Charlotte's action_summary, NOT the raw email subject */}
                            <div className="font-medium text-gray-900">{t.task_title}</div>
                            {/* Second line: metadata only (due, time, priority) — no email body, no sender */}
                            <div className="mt-0.5 flex items-center gap-2 flex-wrap text-xs text-gray-600">
                              {t.due_date && <span>Due {t.due_date}</span>}
                              {t.due_date && t.suggested_time_minutes && <span>·</span>}
                              {t.suggested_time_minutes && (
                                <span>~{t.suggested_time_minutes} min</span>
                              )}
                              {(t.due_date || t.suggested_time_minutes) && <span>·</span>}
                              <span
                                className={`px-1.5 py-0.5 rounded border ${
                                  PRIORITY_COLORS[t.priority] || PRIORITY_COLORS.medium
                                }`}
                              >
                                {t.priority}
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              {(() => {
                                const scheduled =
                                  !!t.google_calendar_event_id || !!scheduledLocal[t.id];
                                const tasked =
                                  !!t.google_tasks_task_id || !!taskedLocal[t.id];
                                const pending = taskActionPending[t.id];
                                return (
                                  <>
                                    <button
                                      onClick={() => handleScheduleTask(t.id)}
                                      disabled={pending === 'schedule' || scheduled}
                                      className={`text-xs px-2.5 py-1 rounded border font-medium ${
                                        scheduled
                                          ? 'bg-green-50 border-green-400 text-green-800'
                                          : 'bg-white border-indigo-300 text-indigo-800 hover:bg-indigo-50'
                                      } disabled:opacity-70`}
                                      title="Create Google Calendar event"
                                    >
                                      {scheduled
                                        ? '✓ Scheduled'
                                        : pending === 'schedule'
                                        ? 'Scheduling…'
                                        : '📅 Schedule'}
                                    </button>
                                    <button
                                      onClick={() => handleAddToTasklist(t.id)}
                                      disabled={pending === 'tasks' || tasked}
                                      className={`text-xs px-2.5 py-1 rounded border font-medium ${
                                        tasked
                                          ? 'bg-green-50 border-green-400 text-green-800'
                                          : 'bg-white border-indigo-300 text-indigo-800 hover:bg-indigo-50'
                                      } disabled:opacity-70`}
                                      title="Add to Google Tasks"
                                    >
                                      {tasked
                                        ? '✓ In Tasks'
                                        : pending === 'tasks'
                                        ? 'Adding…'
                                        : '✓ Add to Tasks'}
                                    </button>
                                    <button
                                      onClick={() => handleDismissTask(t.id)}
                                      className="text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium"
                                      title="Dismiss this task"
                                    >
                                      Dismiss
                                    </button>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Scan modal */}
        {showScanModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-5 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
                <h3 className="text-lg font-semibold text-gray-900">Run AI Task Extraction</h3>
                <button
                  onClick={() => setShowScanModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>
              <div className="p-5">
                <p className="text-sm text-gray-700 mb-4">
                  Paste one or more real emails. Charlotte will extract tasks from each and group
                  them by client.
                </p>

                {sampleEmails.map((s, i) => (
                  <div key={i} className="mb-4 p-3 border border-gray-300 rounded">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Email {i + 1}</span>
                      {sampleEmails.length > 1 && (
                        <button
                          onClick={() =>
                            setSampleEmails(sampleEmails.filter((_, idx) => idx !== i))
                          }
                          className="text-xs text-red-600 hover:underline"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="Client label (optional)"
                        value={s.client_label}
                        onChange={(e) => {
                          const next = [...sampleEmails];
                          next[i].client_label = e.target.value;
                          setSampleEmails(next);
                        }}
                        className="px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        placeholder="From (e.g., jane@acme.com)"
                        value={s.from}
                        onChange={(e) => {
                          const next = [...sampleEmails];
                          next[i].from = e.target.value;
                          setSampleEmails(next);
                        }}
                        className="px-2 py-1.5 text-sm border border-gray-300 rounded"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Subject"
                      value={s.subject}
                      onChange={(e) => {
                        const next = [...sampleEmails];
                        next[i].subject = e.target.value;
                        setSampleEmails(next);
                      }}
                      className="w-full mb-2 px-2 py-1.5 text-sm border border-gray-300 rounded"
                    />
                    <textarea
                      placeholder="Paste email body here…"
                      rows={5}
                      value={s.body}
                      onChange={(e) => {
                        const next = [...sampleEmails];
                        next[i].body = e.target.value;
                        setSampleEmails(next);
                      }}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                    />
                  </div>
                ))}

                <button
                  onClick={() => setSampleEmails([...sampleEmails, { ...DEFAULT_SAMPLE }])}
                  className="text-sm text-indigo-700 hover:underline mb-4"
                >
                  + Add another email
                </button>

                {scanError && (
                  <div className="mb-3 p-2 bg-red-50 border border-red-300 rounded text-sm text-red-700">
                    {scanError}
                  </div>
                )}
                {scanResult && (
                  <div className="mb-3 p-2 bg-green-50 border border-green-300 rounded text-sm text-green-800">
                    Extracted {scanResult.extracted} task{scanResult.extracted !== 1 ? 's' : ''}.
                  </div>
                )}

                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => setShowScanModal(false)}
                    className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleRunScan}
                    disabled={scanning}
                    className="px-4 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {scanning ? 'Scanning…' : 'Extract Tasks'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
